import React, { useState, useEffect, useCallback } from 'react';
import styles from './ManagerListTab.module.css';

import Api from 'utils/api';

import PageComponent from 'components/common/Page/PageComponent';


const ManagerListTab = () => {
  const [managers, setManagers] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);

  // 모달 관련 상태
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    userId: '', username: '', password: '', companyName: '', businessNumber: '',
    name: '', address: '', phoneNumber: '', csNumber: '', email: '', positionId: ''
  });

  const [pageInfo, setPageInfo] = useState({
    pageNumber: 0,
    pageSize: 3,
    totalElements: 0,
    totalPages: 0
  });


  const fetchInitialData = useCallback(async (page, size) => {
    setLoading(true);

    const posData = await Api.get('/manager/position');
    setPositions(posData.positionList || []);

    const data = await Api.get(`/manager?pageNumber=${page}&pageSize=${size}`);
    const { content, totalElements, totalPages } = data.managerList;
    setManagers(content || []);
    setPageInfo(prev => ({
      ...prev,
      totalElements,
      totalPages,
      // pageNumber는 handlePageChange에서 관리하므로 여기서 굳이 또 업데이트하지 않아도 됨
    }));
    setLoading(false);

  }, []);

  useEffect(() => {
    fetchInitialData(pageInfo.pageNumber, pageInfo.pageSize);
  }, [pageInfo.pageNumber, pageInfo.pageSize, fetchInitialData]);


  const handleOpenModal = (mode, data = null) => {
    setModalMode(mode);
    setIsEditMode(false);

    if (mode === 'detail' && data) {
      // 기존 데이터 로드 시 positionId가 확실히 포함되도록 전개 연산자 사용
      setFormData({
        ...data,
        password: '',
        // 만약 data.positionId가 없다면 positions 배열에서 이름을 통해 ID를 찾아 매칭하는 안전장치
        positionId: data.positionId || (positions.find(p => p.positionName === data.positionName)?.positionId || '')
      });
    } else {
      setFormData({
        userId: '', username: '', password: '', companyName: '', businessNumber: '',
        name: '', address: '', phoneNumber: '', csNumber: '', email: '', positionId: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'positionId' ? (value ? Number(value) : '') : value
    }));
  };

  const handleSave = async () => {
    // 저장/수정 로직 (POST 또는 PUT/PATCH)
    const isUpdate = modalMode === 'detail' && isEditMode;
    const method = isUpdate ? 'PATCH' : 'POST';
    if (!formData.username || (!isUpdate && !formData.password) || !formData.positionId) {
      return alert("필수 입력 사항을 확인해주세요.");
    }

    try {
      // 수정일 경우 URL이나 Method가 다를 수 있으나, 기존 코드의 구조를 유지하며 알림만 추가
      let response;
      if (method === 'PATCH') {
        response = await Api.patch('/manager', formData);
      } else {
        response = await Api.post('/manager', formData);
      }

      if (!response) return;

      alert(isUpdate ? "수정이 완료되었습니다." : "등록이 완료되었습니다.");
      setIsModalOpen(false);
      setIsEditMode(false);
      fetchInitialData(pageInfo.pageNumber, pageInfo.pageSize);

    } catch (error) {
      console.error("통신 오류:", error);
      alert("서버와 통신 중 오류가 발생했습니다.");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("정말 이 직원을 삭제하시겠습니까?")) return;

    const data = await Api.delete(`/manager?deleteUserId=${formData.userId}`)

    if (data.code === 200) {
      alert("삭제가 완료되었습니다.");
      setIsModalOpen(false);
      fetchInitialData(); // 목록 새로고침
    } else {
      alert(`삭제 실패: ${data.message || '오류가 발생했습니다.'}`);
    }

  };

  if (loading) return <div className={styles.centerMsg}>직원 정보를 불러오는 중...</div>;

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < pageInfo.totalPages) {
      setPageInfo(prev => ({ ...prev, pageNumber: newPage }));
      window.scrollTo(0, 0);
    }
  };


  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <div className={styles.countInfo}>
          전체 직원 <span className={styles.countNumber}>{pageInfo.totalElements}</span>명
        </div>
        <button className={styles.addBtn} onClick={() => handleOpenModal('add')}>+ 직원 등록</button>
      </div>


      <div className={styles.cardGrid}>
        {managers.map((m) => (
          <div key={m.userId} className={styles.managerCard}>
            <div className={styles.cardHeader}>
              <div>
                <h3 className={styles.managerName}>{m.companyName}</h3>
                <span className={styles.loginIdText}>ID: {m.username}</span>
              </div>
            </div>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}><label>대표자</label><span>{m.name}</span></div>
              <div className={styles.infoItem}><label>직책</label><span className={styles.positionText}>{m.positionName}</span></div>
            </div>
            <div className={styles.cardFooter}>
              <button className={styles.detailBtn} onClick={() => handleOpenModal('detail', m)}> 자세히 </button>
            </div>
          </div>
        ))}
      </div>

      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>{modalMode === 'add' ? '새 직원 등록' : (isEditMode ? '직원 정보 수정' : '직원 상세 정보')}</h3>
              <button className={styles.closeBtn} onClick={() => setIsModalOpen(false)}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formContainer}>
                <div className={styles.inputGroup}>
                  <label>아이디</label>
                  <input name="username" value={formData.username} onChange={handleInputChange} disabled={modalMode === 'detail'} className={styles.modalInput} />
                </div>
                <div className={styles.inputGroup}>
                  <label>비밀번호</label>
                  <input name="password" type="password" className={styles.modalInput} value={formData.password} onChange={handleInputChange} disabled={modalMode === 'detail' && !isEditMode} placeholder={isEditMode ? "변경 시에만 입력" : ""} />
                </div>
                <div className={styles.inputGroup}>
                  <label>상호(이름)</label>
                  <input name="companyName" className={styles.modalInput} value={formData.companyName} onChange={handleInputChange} disabled={modalMode === 'detail' && !isEditMode} />
                </div>
                <div className={styles.inputGroup}>
                  <label>사업자번호</label>
                  <input name="businessNumber" className={styles.modalInput} value={formData.businessNumber} onChange={handleInputChange} disabled={modalMode === 'detail' && !isEditMode} />
                </div>
                <div className={styles.inputGroup}>
                  <label>대표자명</label>
                  <input name="name" className={styles.modalInput} value={formData.name} onChange={handleInputChange} disabled={modalMode === 'detail' && !isEditMode} />
                </div>

                <div className={styles.inputGroup}>
                  <label>직책</label>
                  {modalMode === 'detail' && !isEditMode ? (
                    <input
                      className={styles.modalInput}
                      value={formData.positionName || '직책 없음'}
                      disabled
                    />
                  ) : (
                    <select
                      name="positionId"
                      className={styles.modalInput}
                      // value가 positions의 positionId와 타입/값이 일치해야 기본 선택이 활성화됩니다.
                      value={formData.positionId || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">직책을 선택하세요</option>
                      {positions.map(p => (
                        <option key={p.positionId} value={p.positionId}>
                          {p.positionName}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <label>주소</label>
                  <input name="address" className={styles.modalInput} value={formData.address} onChange={handleInputChange} disabled={modalMode === 'detail' && !isEditMode} />
                </div>
                <div className={styles.inputGroup}>
                  <label>전화번호</label>
                  <input name="phoneNumber" className={styles.modalInput} value={formData.phoneNumber} onChange={handleInputChange} disabled={modalMode === 'detail' && !isEditMode} />
                </div>
                <div className={styles.inputGroup}>
                  <label>고객센터 번호</label>
                  <input name="csNumber" className={styles.modalInput} value={formData.csNumber} onChange={handleInputChange} disabled={modalMode === 'detail' && !isEditMode} />
                </div>
                <div className={styles.inputGroup}>
                  <label>이메일 주소</label>
                  <input name="email" type="email" className={styles.modalInput} value={formData.email} onChange={handleInputChange} disabled={modalMode === 'detail' && !isEditMode} />
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              {modalMode === 'add' ? (
                <>
                  <button className={styles.cancelBtn} onClick={() => setIsModalOpen(false)}>취소</button>
                  <button className={styles.saveBtn} onClick={handleSave}>등록하기</button>
                </>
              ) : (
                <>
                  {isEditMode ? (
                    <>
                      <button className={styles.cancelBtn} onClick={() => setIsEditMode(false)}>취소</button>
                      <button className={styles.saveBtn} onClick={handleSave}>수정 완료</button>
                    </>
                  ) : (
                    <>
                      <button className={styles.editBtn} onClick={() => setIsEditMode(true)}>수정</button>
                      <button className={styles.deleteBtn} onClick={handleDelete}>삭제</button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {pageInfo.totalPages > 0 && (
        <PageComponent pageInfo={pageInfo} onPageChange={handlePageChange} />
      )}

    </div>
  );
};

export default ManagerListTab;